import camelcaseKeys from "camelcase-keys";
import { AxiosError, AxiosResponse } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import config from "config";
import { AXIOS } from "hooks/axios";
import { useError } from "components/status/ErrorProvider";
import { useSuccess } from "components/status/SuccessProvider";
import { ProjectSchema } from "hooks/pavm/base";
import { MetaSchema, PaginationSchema } from "hooks/pagination";

const USER_SERVICE_BASE_URL = config.url.USER_SERVICE;

type useUserServiceProjectsSchema = PaginationSchema & { name?: string };
export const useUserServiceProjects = (body?: useUserServiceProjectsSchema) => {
  return useQuery<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    { data: ProjectSchema[]; meta: MetaSchema }
  >(
    [`USER SERVICE PROJECTS ${JSON.stringify(body)}`],
    () => AXIOS.post(`${USER_SERVICE_BASE_URL}/projects`, body),
    {
      select: (data) => camelcaseKeys(data.data, { deep: true }),
    }
  );
};

type DescriptionSchema = {
  id: string;
  description: string;
};
export const useUserServiceEditDescription = () => {
  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    DescriptionSchema
  >(
    [`USER SERVICE PROJECT UPDATE DESCRIPTION`],
    (props: DescriptionSchema) =>
      AXIOS.post(`${USER_SERVICE_BASE_URL}/projects/edit/description`, props),
    {
      onError: (error) => {
        const message = error.response?.data.message ?? "please try again";
        setError({ message, reset: mutation.reset });
      },
      onSuccess: (data) => {
        setSuccess(`Description updated`);

        queryClient.invalidateQueries({
          predicate: (query) =>
            // @ts-ignore
            query.queryKey[0]?.startsWith("USER SERVICE PROJECTS"),
        });
      },
    }
  );

  return mutation;
};

type ProjectTagsUpdateSchema = {
  id: string;
  tags: string[];
};
export const useUserServiceUpdateTags = () => {
  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<ProjectSchema, any>,
    AxiosError<{ message: string }>,
    ProjectTagsUpdateSchema
  >(
    [`USER SERVICE PROJECT UPDATE TAGS`],
    (props: ProjectTagsUpdateSchema) =>
      AXIOS.post(`${USER_SERVICE_BASE_URL}/projects/edit/tags`, props),
    {
      onError: (error) => {
        const message = error.response?.data.message ?? "please try again";
        setError({ message, reset: mutation.reset });
      },
      onSuccess: (data) => {
        setSuccess(`Tags updated`);

        queryClient.invalidateQueries({
          predicate: (query) =>
            // @ts-ignore
            query.queryKey[0]?.startsWith("USER SERVICE PROJECTS"),
        });
      },
    }
  );

  return mutation;
};

type ProjectManage = {
  note?: string;
  userId: string;
  projectIds: readonly string[];
};

export const useUserServiceProjectsAssign = () => {
  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    ProjectManage
  >(
    [`USER SERVICE PROJECTS ASSIGN`],
    (props: ProjectManage) =>
      AXIOS.post(`${USER_SERVICE_BASE_URL}/projects/assign`, props),
    {
      onError: (error) => {
        const message = error.response?.data.message ?? "please try again";
        setError({ message, reset: mutation.reset });
      },
      onSuccess: (data) => {
        setSuccess(`Project(s) assigned`);

        queryClient.invalidateQueries({
          predicate: (query) =>
            // @ts-ignore
            query.queryKey[0]?.startsWith("USER SERVICE USERS"),
        });
        queryClient.invalidateQueries({
          predicate: (query) =>
            // @ts-ignore
            query.queryKey[0]?.startsWith("USER SERVICE PROJECTS"),
        });
      },
    }
  );

  return mutation;
};

export const useUserServiceProjectsUnassign = () => {
  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    ProjectManage
  >(
    [`USER SERVICE PROJECTS UNASSIGN`],
    (props: ProjectManage) =>
      AXIOS.post(`${USER_SERVICE_BASE_URL}/projects/unassign`, props),
    {
      onError: (error) => {
        const message = error.response?.data.message ?? "please try again";
        setError({ message, reset: mutation.reset });
      },
      onSuccess: (data) => {
        setSuccess(`Project(s) unassigned`);

        queryClient.invalidateQueries({
          predicate: (query) =>
            // @ts-ignore
            query.queryKey[0]?.startsWith("USER SERVICE USERS"),
        });

        queryClient.invalidateQueries({
          predicate: (query) =>
            // @ts-ignore
            query.queryKey[0]?.startsWith("USER SERVICE PROJECTS"),
        });
      },
    }
  );

  return mutation;
};

type ProjectState = { id: string };

export const useUserServiceProjectsActivate = () => {
  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    ProjectState
  >(
    [`USER SERVICE PROJECTS ACTIVATE`],
    (props: ProjectState) =>
      AXIOS.post(`${USER_SERVICE_BASE_URL}/projects/activate`, props),
    {
      onError: (error) => {
        const message = error.response?.data.message ?? "please try again";
        setError({ message, reset: mutation.reset });
      },
      onSuccess: (data) => {
        setSuccess("Project activated");

        queryClient.invalidateQueries({
          predicate: (query) =>
            // @ts-ignore
            query.queryKey[0]?.startsWith("USER SERVICE USERS"),
        });

        queryClient.invalidateQueries({
          predicate: (query) =>
            // @ts-ignore
            query.queryKey[0]?.startsWith("USER SERVICE PROJECTS"),
        });
      },
    }
  );

  return mutation;
};

export const useUserServiceProjectsDeactivate = () => {
  const queryClient = useQueryClient();
  const { setError } = useError();
  const { setSuccess } = useSuccess();

  const mutation = useMutation<
    AxiosResponse<any, any>,
    AxiosError<{ message: string }>,
    ProjectState
  >(
    [`USER SERVICE PROJECTS DEACTIVATE`],
    (props: ProjectState) =>
      AXIOS.post(`${USER_SERVICE_BASE_URL}/projects/deactivate`, props),
    {
      onError: (error) => {
        const message = error.response?.data.message ?? "please try again";
        setError({ message, reset: mutation.reset });
      },
      onSuccess: (data) => {
        setSuccess("Project deactivated");

        queryClient.invalidateQueries({
          predicate: (query) =>
            // @ts-ignore
            query.queryKey[0]?.startsWith("USER SERVICE USERS"),
        });

        queryClient.invalidateQueries({
          predicate: (query) =>
            // @ts-ignore
            query.queryKey[0]?.startsWith("USER SERVICE PROJECTS"),
        });
      },
    }
  );

  return mutation;
};
